import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Badge from '@mui/material/Badge';
import Tooltip from '@mui/material/Tooltip';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Fade from '@mui/material/Fade';
import DashboardHero from './shared/DashboardHero';
import DashboardStatCard from './shared/DashboardStatCard';
import ViewModeBanner from './shared/ViewModeBanner';
import ClinicSelector from './shared/ClinicSelector';

// Icons
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import MessageIcon from '@mui/icons-material/Message';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import NotificationBell from './notifications/NotificationBell';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';

const ClinicStaffDashboard = () => {
  const { profile, signOut } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  
  // Real data - empty until features are built
  const [stats] = useState({
    checkedIn: 0,
    waiting: 0,
    inConsultation: 0,
    completed: 0
  });
  
  const [waitingRoom] = useState([]);
  const [todaysSchedule] = useState([]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Error signing out:', error);
      // Even if signout fails, navigate to login
      navigate('/login', { replace: true });
    }
  };

  return (
    <Box sx={{ 
      flexGrow: 1, 
      bgcolor: 'background.default', 
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <ViewModeBanner />
      {/* Header */}
      <AppBar position="static" color="primary" elevation={2}>
        <Container maxWidth="lg">
          <Toolbar disableGutters>
            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
              <ClinicSelector />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <NotificationBell />
              <Tooltip title="Account">
                <IconButton onClick={handleMenu} color="inherit">
                  <AccountCircleIcon />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                PaperProps={{
                  sx: {
                    mt: 1.5,
                    minWidth: 180,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    borderRadius: 2,
                  }
                }}
              >
                <MenuItem component={Link} to="/profile" onClick={handleClose}>
                  <PersonIcon fontSize="small" sx={{ mr: 1 }} />
                  Profile
                </MenuItem>
                <MenuItem component={Link} to="/settings" onClick={handleClose}>
                  <SettingsIcon fontSize="small" sx={{ mr: 1 }} />
                  Settings
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                  <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                  Logout
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Hero Section */}
      <DashboardHero
        greeting={`Welcome, ${profile?.first_name || 'Staff Member'}!`}
        description="Manage patient intake, scheduling, and clinic operations."
        showVideoCallButton={false}
        showScheduleButton={true}
        scheduleButtonDisabled={false}
        userType="clinic_staff"
      />

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ mb: 4, flex: 1, px: { xs: 1, sm: 2 }, width: '100%', maxWidth: '100%' }}>
        {/* Quick Stats - Clean admin-style cards */}
        <Box sx={{ 
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          flexWrap: 'wrap',
          justifyContent: { xs: 'stretch', sm: 'center' },
          alignItems: 'stretch',
          gap: { xs: 2, sm: 2.5, md: 3 },
          mb: 3,
          width: '100%',
          maxWidth: { xs: '100%', md: '1200px' },
          mx: 'auto',
        }}>
          <Box sx={{ 
            flex: { xs: '1 1 100%', sm: '0 1 calc(50% - 10px)', md: '0 1 calc(20% - 14.4px)' }, 
            minWidth: { xs: '100%', sm: '200px', md: '220px' },
            maxWidth: { xs: '100%', sm: 'calc(50% - 10px)', md: 'calc(20% - 14.4px)' }
          }}>
            <DashboardStatCard
              label="Checked In"
              value={stats.checkedIn}
              sublabel="Total today"
              icon={<CheckCircleIcon color="success" sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' } }} />}
              color="success"
            />
          </Box>
          <Box sx={{ 
            flex: { xs: '1 1 100%', sm: '0 1 calc(50% - 10px)', md: '0 1 calc(20% - 14.4px)' }, 
            minWidth: { xs: '100%', sm: '200px', md: '220px' },
            maxWidth: { xs: '100%', sm: 'calc(50% - 10px)', md: 'calc(20% - 14.4px)' }
          }}>
            <DashboardStatCard
              label="Waiting"
              value={stats.waiting}
              sublabel="In waiting room"
              icon={<HourglassEmptyIcon color="warning" sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' } }} />}
              color="warning"
            />
          </Box>
          <Box sx={{ 
            flex: { xs: '1 1 100%', sm: '0 1 calc(50% - 10px)', md: '0 1 calc(20% - 14.4px)' }, 
            minWidth: { xs: '100%', sm: '200px', md: '220px' },
            maxWidth: { xs: '100%', sm: 'calc(50% - 10px)', md: 'calc(20% - 14.4px)' }
          }}>
            <DashboardStatCard
              label="In Consultation"
              value={stats.inConsultation}
              sublabel="With doctor now"
              icon={<LocalHospitalIcon color="info" sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' } }} />}
              color="info"
            />
          </Box>
          <Box sx={{ 
            flex: { xs: '1 1 100%', sm: '0 1 calc(50% - 10px)', md: '0 1 calc(20% - 14.4px)' }, 
            minWidth: { xs: '100%', sm: '200px', md: '220px' },
            maxWidth: { xs: '100%', sm: 'calc(50% - 10px)', md: 'calc(20% - 14.4px)' }
          }}>
            <DashboardStatCard
              label="Completed"
              value={stats.completed}
              sublabel="Today"
              icon={<EventAvailableIcon color="primary" sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' } }} />}
              color="primary"
            />
          </Box>
          <Box sx={{ 
            flex: { xs: '1 1 100%', sm: '0 1 calc(50% - 10px)', md: '0 1 calc(20% - 14.4px)' }, 
            minWidth: { xs: '100%', sm: '200px', md: '220px' },
            maxWidth: { xs: '100%', sm: 'calc(50% - 10px)', md: 'calc(20% - 14.4px)' }
          }}>
            <DashboardStatCard
              label="Today's Appts"
              value={todaysSchedule.length}
              sublabel="Click to view"
              icon={<CalendarMonthIcon color="secondary" sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' } }} />}
              color="secondary"
              onClick={() => navigate('/appointments?userType=clinic_staff')}
            />
          </Box>
        </Box>

        {/* Quick Actions */}
        <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 4, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, textAlign: 'center' }}>
            Quick Actions
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={6} md={3}>
              <Button 
                fullWidth 
                variant="contained" 
                color="primary"
                startIcon={<PersonAddIcon />}
                sx={{ py: 1.5 }}
              >
                Check-in Patient
              </Button>
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <Button 
                fullWidth 
                variant="contained" 
                color="secondary"
                startIcon={<CalendarMonthIcon />}
                onClick={() => navigate('/appointments?userType=clinic_staff')}
                sx={{ py: 1.5 }}
              >
                Schedule
              </Button>
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <Button 
                fullWidth 
                variant="contained" 
                color="success"
                startIcon={<MonitorHeartIcon />}
                sx={{ py: 1.5 }}
              >
                Record Vitals
              </Button>
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <Button 
                fullWidth 
                variant="contained" 
                color="info"
                startIcon={<MessageIcon />}
                onClick={() => navigate('/messages?userType=clinic_staff')}
                sx={{ py: 1.5 }}
              >
                Send Message
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Waiting Room - Single section */}
        <Paper elevation={0} sx={{ p: 3, borderRadius: 4, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, textAlign: 'center' }}>
            Waiting Room
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {waitingRoom.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                No patients in waiting room
              </Typography>
            ) : (
              waitingRoom.map((patient) => (
                <Paper
                  key={patient.id}
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'background.default',
                    border: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                    '&:hover': {
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: patient.status === 'in-progress' ? 'info.light' : 'primary.light', width: 36, height: 36 }}>
                      <PersonIcon fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {patient.patient}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {patient.time} - {patient.type}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Chip 
                      label={patient.status}
                      color={patient.status === 'in-progress' ? 'info' : 'warning'}
                      size="small"
                    />
                    <Button variant="outlined" size="small">
                      Vitals
                    </Button>
                  </Box>
                </Paper>
              ))
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default ClinicStaffDashboard;
